import type {
  DistributionShare,
  EstateReadinessOutput,
  IntestacyOutput,
  ReadinessItem,
} from "./types.js";

// Plain-English result copy for wills-intestacy. Lives in the rules package so
// every surface (Expo result screen, "my results" list, PDF report) renders
// identical wording from one source.
//
// REGULATORY (binding): every string here is INFORMATION, not advice. We recite
// the statutory default and cite the Act; we never say "you should make a will",
// "save $X", or recommend a product. The two standing caveats are mandatory:
//   * legal:     this is the intestacy default, not legal advice — see a
//                solicitor or your state trustee.
//   * financial: "Educational tool. Not financial advice."
// The NO-AFFILIATE rule means no will-provider name appears in any string here.

/** The mandatory standing disclaimer line — every surface shows it verbatim. */
export const FINANCIAL_DISCLAIMER = "Educational tool. Not financial advice.";

/** The legal-information caveat — paired with the financial disclaimer. */
export const LEGAL_CAVEAT =
  "This is the intestacy default that applies if you die without a will — it is general information, not legal advice. For advice on your situation, see a solicitor or your state trustee.";

/** The estate-only pointer — super / life insurance are a separate question, never modelled here. */
export const ESTATE_ONLY_NOTE =
  "Your superannuation and life insurance may pass outside your estate and are handled separately — they are not part of this intestacy result.";

/**
 * QLD-only caveat. The Succession Act 1981 (Qld) s 36 splits a deceased's
 * spousal entitlement where there is more than one spouse (e.g. a married
 * spouse and a separate de facto partner) by agreement or court order — not a
 * deterministic share. This result models a single partner, so where there are
 * concurrent spouses the partner figure shown is the combined spousal portion
 * before any s 36 division.
 */
export const QLD_MULTIPLE_SPOUSE_NOTE =
  "In Queensland, if more than one person qualifies as your spouse (for example a married spouse and a separate de facto partner), the spouse's share is divided between them by agreement or court order — this result shows the combined spousal portion.";

/** Whole-dollar (with cents where present) AUD. */
export function formatAud(amount: number): string {
  const whole = Number.isInteger(amount);
  return `A$${amount.toLocaleString("en-AU", {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

function pctLabel(fraction: number): string {
  // 0.5 -> "half", 1/3 -> "a third", 2/3 -> "two-thirds".
  const approx = (a: number, b: number) => Math.abs(fraction - a) < 1e-9 || fraction === b;
  if (approx(1, 1)) return "all";
  if (approx(0.5, 0.5)) return "half";
  if (Math.abs(fraction - 1 / 3) < 1e-9) return "a third";
  if (Math.abs(fraction - 2 / 3) < 1e-9) return "two-thirds";
  return `${+(fraction * 100).toFixed(1)}%`;
}

const BENEFICIARY_NOUN: Record<DistributionShare["to"], string> = {
  partner: "your partner",
  children: "your children",
  parents: "your parents",
  siblings: "your siblings",
  crown: "the State (no eligible relatives)",
};

/** Headline — states what the statutory default does, never a saving or a nudge. */
export function distributionHeadline(out: IntestacyOutput): string {
  if (out.passesToCrown) {
    return `With no will and no eligible relatives, your estate passes to the State of ${out.state} (bona vacantia).`;
  }
  if (out.partnerTakesAll) {
    return `Under ${out.act}, if you died without a will your partner would receive your whole estate.`;
  }
  if (out.blendedSplitApplies) {
    return `Under ${out.act}, if you died without a will your partner would NOT receive everything — your estate is split between your partner and your children.`;
  }
  return `Under ${out.act}, this is how your estate would be distributed if you died without a will.`;
}

/** One descriptive line per share — pure recitation of the statutory components. */
export function shareBullet(share: DistributionShare): string {
  const who = BENEFICIARY_NOUN[share.to];
  const parts: string[] = [];
  if (share.personalEffects) parts.push("your personal effects");
  if (share.statutoryLegacy > 0)
    parts.push(`a statutory legacy of ${formatAud(share.statutoryLegacy)}`);
  if (share.residueFraction > 0 && share.residueFraction < 1)
    parts.push(`${pctLabel(share.residueFraction)} of the remainder`);
  else if (share.residueFraction === 1 && parts.length === 0)
    parts.push("the whole estate");
  else if (share.residueFraction === 1) parts.push("the remainder");

  const list =
    parts.length <= 1
      ? (parts[0] ?? "nothing")
      : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;

  const amount =
    share.amount !== null ? ` (${formatAud(share.amount)})` : "";
  return `${capitalise(who)} receives ${list}${amount}.`;
}

function capitalise(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

/** Source-attribution line, naming the Act + section actually in force. */
export function sourceBullet(out: IntestacyOutput, verifiedAt: string): string {
  const dated =
    out.statutoryLegacyEffectiveFrom !== null
      ? ` The statutory legacy of ${formatAud(out.statutoryLegacy)} is the amount in force from ${out.statutoryLegacyEffectiveFrom} (it is indexed and reprices, so it changes over time).`
      : "";
  return `Based on ${out.act}, ${out.distributionRef}, last verified ${verifiedAt}.${dated}`;
}

/** The full ordered set of result bullets, including the mandatory caveats. */
export function distributionBullets(
  out: IntestacyOutput,
  verifiedAt: string,
): string[] {
  const hasPartner = out.shares.some((s) => s.to === "partner");
  return [
    ...out.shares.map(shareBullet),
    sourceBullet(out, verifiedAt),
    ESTATE_ONLY_NOTE,
    ...(out.state === "QLD" && hasPartner ? [QLD_MULTIPLE_SPOUSE_NOTE] : []),
    LEGAL_CAVEAT,
    FINANCIAL_DISCLAIMER,
  ];
}

// --- estate readiness copy ---------------------------------------------------

const DOC_LABEL = {
  will: "a current will",
  enduringPowerOfAttorney: "an enduring power of attorney",
  enduringGuardianship: "an enduring guardianship / medical decision-maker",
  guardianNomination: "a guardian nomination for your children",
} as const;

/** Readiness headline — a factual picture, never a graded recommendation. */
export function readinessHeadline(out: EstateReadinessOutput): string {
  if (!out.actionSuggested) {
    return "You have each of the four key documents in place.";
  }
  const flagged = out.gaps + out.stale;
  return `${flagged} of your ${out.applicable} applicable documents need attention.`;
}

/** One descriptive line per readiness item — states the factual default for a gap. */
export function readinessItemBullet(item: ReadinessItem): string {
  const label = DOC_LABEL[item.doc];
  switch (item.status) {
    case "covered":
      return `You have ${label}.`;
    case "stale":
      return `You have ${label}, but a life event since it was made may have affected it — ${item.defaultIfMissing}`;
    case "gap":
      return `You do not have ${label}. ${item.defaultIfMissing}`;
    case "na":
      return `${capitalise(label)} does not apply to you.`;
  }
}

export function readinessBullets(out: EstateReadinessOutput): string[] {
  return [
    ...out.items.map(readinessItemBullet),
    LEGAL_CAVEAT,
    FINANCIAL_DISCLAIMER,
  ];
}
